import application from "@homer.core/application";
import core from "@homer.core/api";
import isInstanceof from "@homer.utility/validator.isinstanceof";
import Template from "@homer.core/template";
import jsonParse from "@homer.task/json.parse";
import Request from "./request.js";
import obtainStyle from "./style.js";

const classList = document.documentElement.classList;
const flag = "isLoading";
let pending = 0;
let timeout;

const startPending = () => {
    pending++;

    if (!timeout) {
        timeout = setTimeout(() => {
            if (pending > 0) {
                classList.add(flag);
            }
        }, 500);
    }
};

const endPending = () => {
    pending--;

    // Keep the shared timer and loading state alive until every overlapping request has settled.
    if (pending === 0) {
        timeout = clearTimeout(timeout);
        classList.remove(flag);
    }
};

const processHTML = async (data, meta = true) => {
    const template = new Template(data);
    const children = {};
    const promises = [];

    [...template.children].forEach((child) => {
        const id = child.getAttribute("id");
        const isValid = !!(
            id ||
            (child.nodeName === "META" && child.getAttribute("itemprop") === "version") ||
            (child.nodeName === "META" && child.getAttribute("itemprop") === "experiment") ||
            (child.nodeName === "META" && child.getAttribute("itemprop") === "features") ||
            (child.nodeName === "META" && child.getAttribute("itemprop") === "locale") ||
            (child.nodeName === "LINK" && child.getAttribute("rel") === "location")
        );

        if (!isValid) {
            return;
        }

        switch (child.nodeName) {
            case "META":
                if (!children.version && child.getAttribute("itemprop") === "version") {
                    children.version = child.getAttribute("content") || null;
                }

                if (!children.experiment && child.getAttribute("itemprop") === "experiment") {
                    children.experiment = child.getAttribute("content") || null;
                }

                if (!children.features && child.getAttribute("itemprop") === "features") {
                    children.features = child.getAttribute("content") || "";
                }

                if (!children.locale && child.getAttribute("itemprop") === "locale") {
                    children.locale = child.getAttribute("content") || null;
                }

                break;
            case "LINK":
                if (!children.location && child.getAttribute("rel") === "location") {
                    children.location = child.getAttribute("href") || null;
                }

                break;
            case "SCRIPT":
                if (meta && id === "meta" && child.getAttribute("type") === "application/json") {
                    promises.push(
                        jsonParse.execute(child.firstChild.data).then((data) => {
                            children.meta = data;
                        }),
                    );
                }
                break;
            case "TEMPLATE":
                const node = document.importNode(child.content, true);

                children.template = children.template || {};
                children.template[id] = node;

                node.querySelectorAll('meta[itemprop="styles"][content]').forEach((marker) => {
                    marker.parentNode.removeChild(marker);

                    marker.content.split(",").forEach((style) => promises.push(obtainStyle(style)));
                });

                break;
        }
    });

    await Promise.all(promises);

    return children;
};

class Client extends core.Client {
    static base = "/fragment";

    constructor(base) {
        super(base || Client.base);
    }

    execute(request, endpoint) {
        return Promise.resolve()
            .then(async () => {
                if (!isInstanceof(request, Request)) {
                    throw new TypeError("Argument `request` must be of type `Request` from `@homer.api/fragment`");
                }

                return super.execute(request, endpoint, !!(request.flags & Request.flag.isCacheable));
            })
            .then((response) => {
                const { status, data } = response;

                if (status >= 400) {
                    throw response;
                }

                if (response.aborted) {
                    return null;
                }

                return data;
            });
    }

    getHTML(endpoint, { query, layout, showPending = true }, flags = 0) {
        const request = new Request(
            "GET",
            { render: { type: "fragment", query, layout, nonce: application.nonce } },
            { Accept: "text/html" },
            flags | Request.flag.isCacheable | Request.flag.attachClienthints,
            this,
        );

        return Object.defineProperties(
            Promise.resolve(showPending ? startPending() : null)
                .then(() => this.execute(request, endpoint))
                .then((data) => {
                    return data ? processHTML(data) : null;
                })
                .finally(() => {
                    if (showPending) {
                        endPending();
                    }
                }),
            {
                abort: {
                    value: request.abort.bind(request),
                    enumerable: true,
                },
            },
        );
    }

    getJSON(endpoint, { query }, flags = 0) {
        const request = new Request(
            "GET",
            { render: { type: "json", query } },
            { Accept: "application/json" },
            flags | Request.flag.isCacheable,
            this,
        );

        return Object.defineProperties(
            this.execute(request, endpoint).then((data) => {
                if (!data) {
                    throw new Error(`empty response received for json fragment \`${endpoint}\``);
                }

                return data;
            }),
            {
                abort: {
                    value: request.abort.bind(request),
                    enumerable: true,
                },
            },
        );
    }

    getCombination(endpoint, flags = 0) {
        return Promise.resolve(startPending())
            .then(() => {
                return this.execute(
                    new Request(
                        "GET",
                        { render: { type: "combination", query: "content", layout: "shop", nonce: application.nonce } },
                        { Accept: "text/html" },
                        flags | Request.flag.isCacheable | Request.flag.attachClienthints,
                        this,
                    ),
                    endpoint,
                );
            })
            .then((data) => {
                if (!data) {
                    throw new Error(`empty response received for combination fragment \`${endpoint}\``);
                }

                return processHTML(data, false);
            })
            .then((data) => {
                return data?.template?.combination || null;
            })
            .finally(endPending);
    }
}

export default Client;
